// Extended service content for /services/:slug pages.
// Each entry: hero copy, long-form description, benefits, situations, FAQs.

export const SERVICE_CONTENT = {
  "emergency-towing": {
    slug: "emergency-towing",
    path: "/emergency-towing",
    title: "24/7 Emergency Towing",
    h1: "24/7 Emergency Towing in Kirkland & the Eastside",
    metaTitle: "24/7 Emergency Towing in Bellevue & Kirkland | BM Towing",
    metaDesc: "Emergency tow truck service 24/7 across Kirkland, Bellevue, Redmond, Bothell, Kenmore, Woodinville, and Issaquah. Fast dispatch, honest pricing. Call (425) 647-9585.",
    hero: "Broken down at 2 a.m.? We're already on the way.",
    intro: "BM Towing & Recovery keeps a truck on standby every hour of every day. Whether you're stuck on the shoulder of I-405, in a Bellevue parking garage, or in a residential driveway in Kirkland — one call and we roll.",
    benefits: [
      "Real people answering the phone 24/7 — not a robot.",
      "Honest ETA and price quoted before we dispatch.",
      "Right truck for the job: flatbed, wheel-lift or winch.",
      "Serving all of the Eastside — Kirkland, Bellevue, Redmond, Bothell, Kenmore, Woodinville, Issaquah.",
    ],
    situations: [
      "Late-night breakdown on I-405 or SR-520",
      "Battery died in a Bellevue office parking lot",
      "Accident on a Kirkland residential street",
      "Overheated vehicle on the way to Woodinville",
      "Locked keys in the car at a Redmond restaurant",
    ],
    faqs: [
      { q: "Are you really available 24/7?", a: "Yes — every hour of every day, including holidays. Call (425) 647-9585 anytime." },
      { q: "How fast can a truck get to me?", a: "Response times depend on traffic, weather, and where you are on the Eastside. We give you an honest ETA on the phone before we dispatch." },
      { q: "How much does a 24/7 tow cost?", a: "Our base hook-up starts at $195 for standard vehicles ($230 for luxury/exotic) plus mileage. We quote the total before the truck rolls — no surprise fees." },
      { q: "Do you tow after accidents?", a: "Yes. We handle post-collision recovery carefully and can coordinate directly with police or your insurance." },
      { q: "Do you take credit cards?", a: "Yes. We accept major cards and cash. Payment is handled at drop-off." },
    ],
  },

  "flatbed-towing": {
    slug: "flatbed-towing",
    path: "/flatbed-towing",
    title: "Flatbed Towing",
    h1: "Flatbed Towing in Kirkland, Bellevue & the Eastside",
    metaTitle: "Flatbed Tow Truck in Bellevue & Kirkland, WA | BM Towing",
    metaDesc: "Safe flatbed towing for luxury cars, AWD vehicles, low-clearance sports cars, and long-distance transport across the Eastside. Call BM Towing (425) 647-9585.",
    hero: "The safest way to move a car that shouldn't be dragged.",
    intro: "Flatbed towing lifts your vehicle completely off the ground — no drivetrain contact, no scraped bumpers. It's the right choice for luxury vehicles, AWD/4WD, low-clearance sports cars, exotics, motorcycles, and long-distance transport.",
    benefits: [
      "Zero wear on your drivetrain (AWD/4WD safe).",
      "No bumper or splitter damage on low-clearance vehicles.",
      "Strapped down at four points for highway-safe transport.",
      "Ideal for dealer transfers, auction pickups, and shop drops.",
    ],
    situations: [
      "Porsche, Tesla, BMW M-line or Mercedes AMG transport",
      "AWD Subaru or Audi that shouldn't be dragged",
      "Lowered vehicles that scrape on tilt-loading",
      "Non-running project cars and auction pickups",
      "Long-distance Kirkland → dealer transfers",
    ],
    faqs: [
      { q: "Why choose flatbed over wheel-lift?", a: "Flatbed keeps all four wheels off the ground, so there's no drivetrain wear (important for AWD/4WD) and no risk of front-end damage on low-clearance cars." },
      { q: "Can you flatbed a car with no keys?", a: "Yes — we can winch a non-running or keyless vehicle onto the flatbed and secure it safely." },
      { q: "What does flatbed towing cost?", a: "Base flatbed hook-up is $195 for standard vehicles / $230 for luxury or premium cars, plus mileage. We quote the full price on the phone before we roll." },
      { q: "Do you tow motorcycles on a flatbed?", a: "Yes. We use motorcycle-specific chocks and soft straps to protect the frame and forks." },
      { q: "How far can you tow?", a: "Anywhere the customer needs — from local drops on the Eastside to long-distance dealer transfers. We'll quote the mileage up-front." },
    ],
  },

  "roadside-assistance": {
    slug: "roadside-assistance",
    path: "/roadside-assistance",
    title: "Roadside Assistance",
    h1: "Roadside Assistance in Kirkland & the Eastside",
    metaTitle: "Roadside Assistance in Bellevue & Kirkland | BM Towing",
    metaDesc: "Fast roadside help — jump starts, tire changes, lockouts, fuel delivery — across Kirkland, Bellevue, Redmond, Bothell, Kenmore, Woodinville, Issaquah. Call (425) 647-9585.",
    hero: "Stuck on the side of the road? We'll come to you.",
    intro: "Not every problem needs a tow. Sometimes it's a dead battery, a flat, an empty tank, or keys locked in the car. BM Towing sends a driver with the right gear to get you moving again — often in the same visit.",
    benefits: [
      "Fast dispatch across the Eastside.",
      "Jump starts, tire changes, lockouts, fuel delivery — one call.",
      "Fair, quoted pricing before we roll.",
      "Trained operators with the right tools for the job.",
    ],
    situations: [
      "Battery died in a Bellevue parking garage",
      "Flat tire on I-405 southbound",
      "Ran out of gas near Woodinville",
      "Keys locked in a running car at a Kirkland Starbucks",
      "Cold-weather no-start in a Bothell driveway",
    ],
    faqs: [
      { q: "What kinds of roadside help do you provide?", a: "Jump starts, tire changes (with your spare), fuel delivery, lockouts, and light mechanical assessment. If we can't fix it roadside, we'll tow it." },
      { q: "How much is a jump start or lockout?", a: "We quote every job on the phone based on location and complexity. No hidden fees." },
      { q: "Do you deliver fuel?", a: "Yes — enough to get you to the nearest gas station. You pay for the fuel plus the service call." },
      { q: "Can you change a tire without a spare?", a: "If you don't have a spare, we'll tow you to a tire shop instead. That's usually the fastest path." },
    ],
  },

  "accident-towing": {
    slug: "accident-towing",
    title: "Accident Towing & Recovery",
    h1: "Accident Towing & Recovery in Kirkland & Bellevue",
    metaTitle: "Accident Tow & Recovery in Bellevue & Kirkland | BM Towing",
    metaDesc: "Careful post-accident towing and recovery across the Eastside. We coordinate with police and insurance. Call BM Towing (425) 647-9585.",
    hero: "After a collision, the last thing you should worry about is your car.",
    intro: "Accidents are stressful. We show up fast, secure your vehicle, and get it moved to a shop, your home, or an impound. We can coordinate directly with responding officers and communicate with your insurance company.",
    benefits: [
      "Careful handling of damaged vehicles.",
      "Can coordinate with police at the scene.",
      "Flatbed available for undriveable vehicles.",
      "Fair, transparent pricing — no gouging.",
    ],
    situations: [
      "Fender bender at a Kirkland intersection",
      "Collision on I-405 near Bellevue",
      "Off-road wreck near Issaquah",
      "Rear-ended on SR-520 during rush hour",
      "Weather-related accident on the Eastside",
    ],
    faqs: [
      { q: "Do you work with insurance companies?", a: "Yes. We can provide invoices and details insurance adjusters need for your claim." },
      { q: "Will you show up if the police called for a tow?", a: "If we're the shop dispatched, absolutely — and we'll coordinate with the officer on scene." },
      { q: "Can you tow a vehicle that won't roll?", a: "Yes — that's what our flatbed and winch are for. We handle non-rolling and severely damaged vehicles." },
    ],
  },

  "winch-outs": {
    slug: "winch-outs",
    title: "Winch-Outs & Recovery",
    h1: "Winch-Out Recovery in Kirkland, Bellevue & the Eastside",
    metaTitle: "Winch-Out & Recovery in Bellevue & Kirkland | BM Towing",
    metaDesc: "Vehicle stuck in mud, snow, a ditch, or off-road? BM Towing winches you out safely across the Eastside. Call (425) 647-9585.",
    hero: "Stuck in mud, snow, or off the shoulder? We'll pull you out.",
    intro: "Whether your car slid off an icy road, backed into a ditch, or got stuck in mud, our winch-out service uses the right equipment to recover your vehicle without additional damage.",
    benefits: [
      "Heavy-duty winch capable of most recoveries.",
      "Careful hook-up to avoid frame or body damage.",
      "Same call can transition into a tow if needed.",
      "Priced by difficulty — quoted before we start.",
    ],
    situations: [
      "Slid off SR-520 during a snowstorm",
      "Backed into a ditch on an Issaquah rural road",
      "Stuck in mud on a construction site",
      "Off-shoulder recovery on I-405",
      "Sunk in soft ground at a Woodinville property",
    ],
    faqs: [
      { q: "How is winch-out priced?", a: "By difficulty and time. A simple ditch pull is different from a mud-buried recovery. We quote after seeing photos or when we arrive." },
      { q: "Will winching damage my car?", a: "We hook to structural points and pull carefully. When done correctly, winching a stuck vehicle is safer than trying to power out." },
      { q: "Do you handle off-road recoveries?", a: "For accessible situations, yes. Deep off-road or heavy-duty recoveries may require specialized equipment — we'll be honest on the phone." },
    ],
  },

  "jump-start": {
    slug: "jump-start",
    path: "/jump-start",
    title: "Jump Start Service",
    h1: "Jump Start Service in Kirkland & the Eastside",
    metaTitle: "Jump Start Service in Bellevue & Kirkland | BM Towing",
    metaDesc: "Dead battery? We jump-start cars, SUVs and trucks across Kirkland, Bellevue, Redmond, Bothell, Kenmore, Woodinville and Issaquah. Call (425) 647-9585.",
    hero: "Dead battery? A quick call gets you moving again.",
    intro: "Dead batteries are one of the most common reasons drivers call us. We show up with a portable jump pack, safely connect it to your vehicle, and get you started — usually in a few minutes.",
    benefits: [
      "Safe on modern vehicles with sensitive electronics.",
      "Fast dispatch — often the same trip as another call.",
      "If the battery is truly dead, we'll advise on next steps.",
      "Flat rate quoted on the phone.",
    ],
    situations: [
      "Battery died overnight in a Kirkland driveway",
      "Left the lights on in a Bellevue office lot",
      "Cold morning no-start in Bothell",
      "Battery drain after a long trip",
    ],
    faqs: [
      { q: "Will jump-starting hurt my car?", a: "Not when done properly. We use a modern jump pack that's safer than jumper cables on cars with sensitive electronics." },
      { q: "What if the jump doesn't hold?", a: "Then the battery is likely at end of life. We can tow you to a shop or you can drive to the nearest auto parts store to replace it." },
      { q: "Do you sell batteries?", a: "We don't stock batteries, but we can tow you to a shop that does." },
    ],
  },

  "car-lockout": {
    slug: "car-lockout",
    path: "/car-lockout",
    title: "Car Lockouts",
    h1: "Car Lockout Service in Kirkland & the Eastside",
    metaTitle: "Car Lockout Service in Bellevue & Kirkland | BM Towing",
    metaDesc: "Locked out of your car? BM Towing unlocks most vehicles without damage across the Eastside. Call (425) 647-9585.",
    hero: "Locked out? We'll get you back inside.",
    intro: "Keys locked in the car, at the store, or at home? Our lockout service uses professional tools to unlock most vehicles without damaging the door, weather-stripping, or window.",
    benefits: [
      "No damage to your door or window in most cases.",
      "Works on most makes and models.",
      "Fast — usually done within minutes on scene.",
      "Priced honestly before we start.",
    ],
    situations: [
      "Keys locked inside at a Bellevue Costco",
      "Left keys in the car at a Kirkland gym",
      "Keys locked in a running car",
      "Purse or bag on the seat, doors locked",
    ],
    faqs: [
      { q: "Can you unlock any car?", a: "We can unlock most modern vehicles. Some vehicles with high-security keys or damaged locks may need a locksmith — we'll tell you honestly." },
      { q: "Will this damage my car?", a: "In the vast majority of cases, no. Our tools are made for exactly this. In rare cases, we'll advise before proceeding." },
      { q: "How fast is a lockout?", a: "Response time depends on where you are. The actual unlock is usually just a few minutes on scene." },
      { q: "Do you make keys?", a: "No — we can unlock the vehicle, but replacement keys require a locksmith or dealer." },
    ],
  },

  "tire-change": {
    slug: "tire-change",
    path: "/tire-change",
    title: "Flat Tire & Tire Change",
    h1: "Flat Tire Service in Kirkland & the Eastside",
    metaTitle: "Flat Tire & Tire Change in Bellevue & Kirkland | BM Towing",
    metaDesc: "Flat tire on the road? We change tires roadside across the Eastside — you supply the spare, we do the work. Call (425) 647-9585.",
    hero: "Flat on the shoulder? We'll swap your spare in minutes.",
    intro: "A flat tire on I-405 or a dark Bellevue street is dangerous. Instead of jacking it up yourself, call us — we bring the right tools and get you back on the road quickly and safely.",
    benefits: [
      "Faster and safer than doing it yourself on the shoulder.",
      "Trained operators using proper equipment.",
      "If you don't have a spare, we tow you to a tire shop.",
    ],
    situations: [
      "Blowout on I-405 or SR-520",
      "Slow leak on a Kirkland residential street",
      "Curb damage in a Bellevue parking garage",
      "Nail-in-tread in a Redmond neighborhood",
    ],
    faqs: [
      { q: "Do I need a spare tire?", a: "Yes — we install your spare. If you don't have one or it's damaged, we'll tow you to a tire shop instead." },
      { q: "Can you fix the tire?", a: "We swap for your spare. Actual tire repair happens at a tire shop." },
      { q: "Is roadside tire change safer than doing it myself?", a: "On a busy freeway shoulder — absolutely. Trained operators with proper positioning and lights reduce risk significantly." },
    ],
  },

  "motorcycle-towing": {
    slug: "motorcycle-towing",
    path: "/motorcycle-towing",
    title: "Motorcycle Towing",
    h1: "Motorcycle Towing in Kirkland & the Eastside",
    metaTitle: "Motorcycle Towing in Bellevue & Kirkland, WA | BM Towing",
    metaDesc: "Secure motorcycle transport by drivers who ride. Bellevue, Kirkland, Redmond, Issaquah, Mercer Island, Newcastle, Sammamish. Call (425) 647-9585.",
    hero: "Riders trust us with their bikes — because we ride too.",
    intro: "Motorcycles need different equipment and a careful hand. We use motorcycle-specific chocks, soft straps, and flatbed transport to move your bike without damage to the frame, forks, or plastics.",
    benefits: [
      "Motorcycle-specific chocks and soft straps.",
      "Flatbed transport — bike stays vertical.",
      "Trained on sport, cruiser, touring, and adventure bikes.",
      "Careful handling of custom or high-value bikes.",
    ],
    situations: [
      "Breakdown on a country road near Sammamish",
      "Dropped bike needing shop transport",
      "Long-distance bike delivery",
      "Post-crash motorcycle recovery",
    ],
    faqs: [
      { q: "Do you tow any type of motorcycle?", a: "Yes — sport bikes, cruisers, tourers, adventure, and most trikes. Call with your bike model and pickup for a quote." },
      { q: "Will my fairings or plastics get damaged?", a: "We use soft straps and motorcycle chocks specifically to avoid fairing pressure and frame damage." },
      { q: "How much is motorcycle towing?", a: "Priced per job depending on pickup, drop-off, and bike specifics. Quoted on the phone." },
    ],
  },

  "winch-out-service": {
    slug: "winch-out-service",
    path: "/winch-out-service",
    title: "Winch-Out Service",
    h1: "Winch-Out & Recovery Service in Kirkland & the Eastside",
    metaTitle: "Winch-Out Service in Bellevue & Kirkland | BM Towing",
    metaDesc: "Vehicle stuck in mud, snow, a ditch, or off-road? BM Towing & Recovery winches you out safely across the Eastside. Call (425) 647-9585.",
    hero: "Stuck in mud, snow, or off the shoulder? We'll pull you out.",
    intro: "Whether your car slid off an icy road, backed into a ditch, or got stuck in mud, our winch-out service uses rated recovery gear and correct pull angles to recover your vehicle without additional damage.",
    benefits: [
      "Rated recovery gear — no rope-and-hope pulls.",
      "Correct hook points to protect your frame and body panels.",
      "Same truck can transition to a tow if you don't want to drive after recovery.",
      "Priced by difficulty — quoted before we start.",
    ],
    situations: [
      "Slid off SR-520 during a snowstorm",
      "Backed into a ditch on an Issaquah rural road",
      "Stuck in mud on a construction site",
      "Off-shoulder recovery on I-405",
      "Sunk in soft ground on private property",
    ],
    faqs: [
      { q: "How is winch-out priced?", a: "By difficulty and time. A simple ditch pull is different from a mud-buried recovery. We quote after seeing photos or when we arrive." },
      { q: "Will winching damage my car?", a: "We hook to structural points and pull carefully. When done correctly, winching a stuck vehicle is safer than trying to power out." },
      { q: "Do you handle off-road recoveries?", a: "For accessible situations, yes. Deep off-road or heavy-recovery scenarios may need a specialized rig — we'll tell you honestly." },
    ],
  },

  "equipment-transportation": {
    slug: "equipment-transportation",
    path: "/equipment-transportation",
    title: "Equipment Transportation",
    h1: "Equipment Transportation in Kirkland, Bellevue & the Eastside",
    metaTitle: "Equipment Transportation Bellevue & Kirkland | BM Towing",
    metaDesc: "Flatbed equipment transport — toolboxes, generators, small skid-steers, safes, forklifts, ATVs, small trailers. Serving Kirkland, Bellevue, Redmond & the Eastside. Call (425) 647-9585.",
    hero: "Not just cars — we haul equipment too.",
    intro: "Our flatbed doesn't only move vehicles. We regularly haul heavy toolboxes, generators, small skid-steers, forklifts, safes, ATVs, side-by-sides, small trailers, and other industrial or contractor equipment across Kirkland, Bellevue, Redmond, and the greater Eastside. If it fits on the deck and can be strapped down safely, we can move it.",
    benefits: [
      "Flatbed with rated tie-downs and four-corner strap points.",
      "Winch loading for equipment that doesn't roll or drive.",
      "Careful placement for balance and weight distribution.",
      "One-call service — pickup, transport, drop-off scheduled around your day.",
    ],
    situations: [
      "Contractor toolbox from a Kirkland job site to a shop",
      "Small skid-steer or mini excavator between Eastside sites",
      "Generator delivery for a Bellevue event or construction pad",
      "Safe, vault, or heavy cabinet relocation",
      "ATV / side-by-side transport to a trailhead or dealer",
      "Small utility trailer moved between homes",
    ],
    faqs: [
      { q: "What kinds of equipment can you haul?", a: "Toolboxes, generators, safes, small skid-steers, forklifts, ATVs, side-by-sides, small trailers, motorcycles, and most items that fit on a standard flatbed and can be strapped down safely. Call us with the item, dimensions, and approximate weight for a quote." },
      { q: "Is there a weight limit?", a: "Yes, our flatbed has a rated capacity. Give us the equipment weight on the phone and we'll tell you if we can haul it or refer you to a heavy-haul partner for larger loads." },
      { q: "Can you winch-load equipment that doesn't roll?", a: "Yes — our flatbed has a winch that can pull non-rolling equipment onto the deck. We evaluate the hook points on the phone before dispatch." },
      { q: "Do you handle scheduled commercial transports?", a: "Yes. We work with contractors, dealerships, and shops across the Eastside. Call to set up recurring or scheduled pickups." },
      { q: "How is equipment transportation priced?", a: "Priced per job based on pickup and drop-off locations, weight, and load complexity. We quote the full price on the phone before dispatching a truck." },
    ],
  },

  "long-distance-towing": {
    slug: "long-distance-towing",
    path: "/long-distance-towing",
    title: "Long-Distance Towing",
    h1: "Long-Distance Towing from Kirkland, Bellevue & the Eastside",
    metaTitle: "Long-Distance Towing in Bellevue & Kirkland | BM Towing",
    metaDesc: "Long-distance flatbed towing from Kirkland, Bellevue & the Eastside — dealer transfers, cross-state transports, scheduled deliveries. Call (425) 647-9585 for a mileage quote.",
    hero: "Need a car moved a long way? Book a flatbed, not a headache.",
    intro: "Long-distance towing is one of our specialties. Whether you're moving a car from Kirkland to Portland, delivering a purchased vehicle across Washington, transporting a project car to a shop in another county, or scheduling a dealer transfer, we handle the trip on a flatbed so your vehicle arrives exactly as it left — no drivetrain wear, no bumper damage, no extra miles on the odometer.",
    benefits: [
      "Flatbed transport — zero miles added to your car.",
      "Scheduled pickup and drop-off windows so you can plan around it.",
      "Four-point strap tie-downs and protective wheel straps.",
      "Written mileage quote before we roll — no surprise fees.",
    ],
    situations: [
      "Kirkland or Bellevue to Portland, Vancouver WA, or Spokane",
      "Cross-state dealer or auction transfers",
      "Non-running project car from the Eastside to a specialist shop",
      "Buyer delivery after a private-party or online sale",
      "Snowbird or relocation moves — car shipped ahead",
      "Track-day or event car transport to and from the venue",
    ],
    faqs: [
      { q: "How far will you tow?", a: "We handle long-distance transports across Washington and neighboring states on request. Call with your pickup and drop-off locations and we'll quote mileage plus timing." },
      { q: "How is long-distance towing priced?", a: "By hook-up rate plus per-mile mileage, quoted end-to-end on the phone. Long-distance jobs are typically scheduled, so we lock in the price before the day of transport." },
      { q: "Can you transport a non-running vehicle long-distance?", a: "Yes. Our flatbed has a winch for non-running or keyless vehicles, and we strap the car at manufacturer-designated tow points for the entire trip." },
      { q: "Do you offer enclosed long-distance transport?", a: "We operate open flatbeds. For enclosed cross-country hauls we can refer you to a partner carrier." },
      { q: "How far in advance should I schedule?", a: "For a specific pickup or drop-off day, a few days of notice is best. For urgent or same-day long-distance moves, call us and we'll do what we can." },
    ],
  },

  "winch-outs-alias": null,
};

// Ordering / listing helpers
export const SERVICE_SLUGS = Object.keys(SERVICE_CONTENT).filter((s) => SERVICE_CONTENT[s]);
export function getService(slug) {
  return SERVICE_CONTENT[slug] || null;
}

export const PRIMARY_SERVICE_SLUGS = [
  "emergency-towing",
  "flatbed-towing",
  "roadside-assistance",
  "jump-start",
  "car-lockout",
  "tire-change",
  "winch-out-service",
];

export const SERVICE_SLUG_ALIASES = {
  "24-7-towing": "emergency-towing",
  "jump-starts": "jump-start",
  "lockouts": "car-lockout",
  "flat-tire": "tire-change",
  "winch-outs": "winch-out-service",
};
